import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Bouton, Etiquette, Liste } from '../components/ui/Base'
import { Bandeau, EtatVide, MessageErreur } from '../components/ui/Etats'
import { Confirmation } from '../components/ui/Modale'
import { Squelette, SqueletteTableau } from '../components/ui/Squelette'
import { useToast } from '../components/ui/Toasts'
import { api } from '../lib/api/client'
import { cles } from '../lib/api/cles'
import type { ApercuFacturation, Page, ReleveHonoraires, Utilisateur } from '../lib/api/types'
import { useSession } from '../lib/auth'
import { formatDate, formatDuree, formatMontant } from '../lib/formatters'

/**
 * Facturation — aperçu et relevés.
 *
 * Le document s'appelle partout **« Relevé d'activité et d'honoraires »**
 * (§2.2). Ce n'est pas une quittance, et surtout pas un bulletin de paie : un
 * infirmier libéral n'est pas un salarié.
 */
export function Facturation() {
  const [parametres, setParametres] = useSearchParams()
  const { utilisateur, estTitulaire } = useSession()
  const queryClient = useQueryClient()
  const toast = useToast()
  const [aEmettre, setAEmettre] = useState<ReleveHonoraires | null>(null)

  const debut = parametres.get('debut') ?? '2026-07-01'
  const fin = parametres.get('fin') ?? '2026-07-31'
  const utilisateurId = parametres.get('utilisateur_id') ?? utilisateur?.id ?? ''

  const requeteInfirmiers = useQuery({
    queryKey: cles.utilisateurs.liste(),
    queryFn: ({ signal }) => api.get<Page<Utilisateur>>('/utilisateurs', signal),
    enabled: estTitulaire,
  })

  const requeteApercu = useQuery({
    queryKey: cles.facturation.apercu(utilisateurId, debut, fin),
    queryFn: ({ signal }) =>
      api.get<ApercuFacturation>(
        `/facturation/apercu?utilisateur_id=${utilisateurId}&debut=${debut}&fin=${fin}`,
        signal,
      ),
    enabled: Boolean(utilisateurId),
  })

  const requeteReleves = useQuery({
    queryKey: cles.releves.liste(),
    queryFn: ({ signal }) => api.get<Page<ReleveHonoraires>>('/releves', signal),
  })

  const genererReleve = useMutation({
    mutationFn: () =>
      api.post<ReleveHonoraires>('/releves', {
        utilisateur_id: utilisateurId,
        periode_debut: debut,
        periode_fin: fin,
      }),
    onSuccess: (releve) => {
      toast(`Brouillon ${releve.numero} créé.`, 'succes')
      void queryClient.invalidateQueries({ queryKey: cles.releves.tout })
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Génération impossible.', 'erreur'),
  })

  const emettre = useMutation({
    mutationFn: (id: string) => api.post<ReleveHonoraires>(`/releves/${id}/emettre`),
    onSuccess: (releve) => {
      toast(`${releve.numero} émis — il est désormais immuable.`, 'succes')
      setAEmettre(null)
      void queryClient.invalidateQueries({ queryKey: cles.releves.tout })
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Émission impossible.', 'erreur'),
  })

  async function telechargerPdf(releve: ReleveHonoraires) {
    try {
      const { blob, nomFichier } = await api.telecharger(`/releves/${releve.id}/pdf`)
      const url = URL.createObjectURL(blob)
      const lien = document.createElement('a')
      lien.href = url
      // Le nom vient du serveur (§6). Le réécrire ici produirait des fichiers
      // dont le nom ne correspond plus au document.
      lien.download = nomFichier ?? `${releve.numero}.pdf`
      lien.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Téléchargement impossible.', 'erreur')
    }
  }

  function changer(cle: string, valeur: string) {
    const suivants = new URLSearchParams(parametres)
    if (valeur) suivants.set(cle, valeur)
    else suivants.delete(cle)
    setParametres(suivants, { replace: true })
  }

  const apercu = requeteApercu.data
  const vide = apercu !== undefined && apercu.nb_interventions === 0

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Facturation</h1>
        <p className="text-sm text-slate-600">Relevé d&apos;activité et d&apos;honoraires</p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-3">
        {estTitulaire ? (
          <div>
            <label htmlFor="infirmier" className="mb-1 block text-xs font-medium text-slate-600">
              Infirmier
            </label>
            <Liste
              id="infirmier"
              value={utilisateurId}
              onChange={(e) => changer('utilisateur_id', e.target.value)}
            >
              {(requeteInfirmiers.data?.items ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.prenom} {u.nom}
                </option>
              ))}
            </Liste>
          </div>
        ) : null}
        <div>
          <label htmlFor="debut" className="mb-1 block text-xs font-medium text-slate-600">Du</label>
          <input id="debut" type="date" value={debut} onChange={(e) => changer('debut', e.target.value)}
                 className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
        </div>
        <div>
          <label htmlFor="fin" className="mb-1 block text-xs font-medium text-slate-600">Au</label>
          <input id="fin" type="date" value={fin} onChange={(e) => changer('fin', e.target.value)}
                 className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
        </div>
      </div>

      <Bandeau ton="info">
        Cet aperçu est calculé à la volée. <strong>Il ne fige rien</strong> : seule la génération
        d&apos;un relevé enregistre les montants.
      </Bandeau>

      {requeteApercu.isPending ? (
        <Squelette lignes={6} />
      ) : requeteApercu.isError ? (
        <MessageErreur erreur={requeteApercu.error} onReessayer={() => requeteApercu.refetch()} />
      ) : !apercu ? null : vide ? (
        <EtatVide
          icone="🧾"
          titre="Aucune intervention réalisée sur la période"
          description="Seules les interventions au statut « réalisée » entrent dans le calcul."
        />
      ) : (
        <>
          {/* Libellés adaptés au mode de rémunération. */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Carte titre="Interventions" valeur={String(apercu.nb_interventions)} />
            <Carte titre="Durée facturable" valeur={formatDuree(apercu.duree_totale_minutes)} />
            {apercu.mode_remuneration === 'HORAIRE' ? (
              <>
                <Carte titre="Taux horaire" valeur={`${formatMontant(apercu.taux_applique)}/h`} />
                <Carte titre="Montant dû" valeur={formatMontant(apercu.montant_du)} accent />
              </>
            ) : (
              <>
                <Carte titre="Honoraires" valeur={formatMontant(apercu.montant_honoraires)} />
                <Carte
                  titre={`Rétrocédé au cabinet (${apercu.taux_applique} %)`}
                  valeur={formatMontant(apercu.montant_retrocede_cabinet)}
                />
              </>
            )}
          </div>

          {apercu.mode_remuneration === 'RETROCESSION' ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Carte titre="Déplacements (IFD + IK)" valeur={formatMontant(apercu.montant_deplacements)} />
              <Carte titre="Montant dû" valeur={formatMontant(apercu.montant_du)} accent />
            </div>
          ) : null}

          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <caption className="sr-only">Détail des interventions facturables</caption>
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="px-4 py-2.5">Date</th>
                  <th scope="col" className="px-4 py-2.5">Patient</th>
                  <th scope="col" className="px-4 py-2.5">Soins</th>
                  <th scope="col" className="px-4 py-2.5 text-right">Durée</th>
                  <th scope="col" className="px-4 py-2.5 text-right">Montant</th>
                </tr>
              </thead>
              <tbody>
                {apercu.lignes.map((ligne, i) => (
                  <tr key={`${ligne.date}-${i}`} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-2 tabular-nums text-slate-600">
                      {formatDate(ligne.date)} {ligne.heure_debut}
                    </td>
                    <td className="px-4 py-2 text-slate-800">
                      {ligne.patient_nom} {ligne.patient_prenom}
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      {ligne.soins.join(', ')}
                      {ligne.motif_majoration ? (
                        <Etiquette ton="attention">+ {ligne.motif_majoration}</Etiquette>
                      ) : null}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-600">
                      {formatDuree(ligne.duree_facturable_minutes)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium text-slate-900">
                      {formatMontant(Number(ligne.montant) + Number(ligne.majoration ?? 0))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Bouton onClick={() => genererReleve.mutate()} disabled={genererReleve.isPending}>
            {genererReleve.isPending ? 'Génération…' : 'Générer un relevé (brouillon)'}
          </Bouton>
        </>
      )}

      {/* ── Relevés existants ── */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold text-slate-900">Relevés</h2>
        {requeteReleves.isPending ? (
          <SqueletteTableau colonnes={5} lignes={3} />
        ) : requeteReleves.isError ? (
          <MessageErreur erreur={requeteReleves.error} />
        ) : (requeteReleves.data?.items.length ?? 0) === 0 ? (
          <EtatVide icone="📄" titre="Aucun relevé" description="Générez un brouillon depuis l’aperçu ci-dessus." />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <caption className="sr-only">Relevés d&apos;activité et d&apos;honoraires</caption>
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="px-4 py-2.5">Numéro</th>
                  <th scope="col" className="px-4 py-2.5">Période</th>
                  <th scope="col" className="px-4 py-2.5 text-right">Montant dû</th>
                  <th scope="col" className="px-4 py-2.5">Statut</th>
                  <th scope="col" className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {requeteReleves.data?.items.map((releve) => (
                  <tr key={releve.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-2.5 font-mono text-slate-800">{releve.numero}</td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {formatDate(releve.periode_debut)} – {formatDate(releve.periode_fin)}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium">
                      {formatMontant(releve.montant_du)}
                    </td>
                    <td className="px-4 py-2.5">
                      <Etiquette
                        ton={releve.statut === 'EMIS' ? 'info' : releve.statut === 'REGLE' ? 'succes' : 'neutre'}
                      >
                        {releve.statut}
                      </Etiquette>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-2">
                        {releve.statut === 'BROUILLON' ? (
                          <Bouton variante="secondaire" onClick={() => setAEmettre(releve)}>
                            Émettre
                          </Bouton>
                        ) : null}
                        <Bouton
                          variante="discret"
                          disabled={!releve.fichier_pdf}
                          title={releve.fichier_pdf ? undefined : 'Le PDF est produit à l’émission du relevé.'}
                          onClick={() => telechargerPdf(releve)}
                        >
                          PDF
                        </Bouton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Confirmation
        ouverte={aEmettre !== null}
        titre={`Émettre le relevé ${aEmettre?.numero ?? ''} ?`}
        description="Un relevé émis devient immuable : on ne le recalcule jamais. Pour corriger une erreur, il faudra l’annuler et en émettre un autre."
        libelleConfirmation="Émettre définitivement"
        enCours={emettre.isPending}
        onConfirmer={() => aEmettre && emettre.mutate(aEmettre.id)}
        onAnnuler={() => setAEmettre(null)}
      />
    </div>
  )
}

function Carte({ titre, valeur, accent }: { titre: string; valeur: string; accent?: boolean }) {
  return (
    <div className={`rounded-lg border p-3 ${accent ? 'border-sky-300 bg-sky-50' : 'border-slate-200 bg-white'}`}>
      <p className="text-xs uppercase tracking-wide text-slate-500">{titre}</p>
      <p className={`mt-1 text-xl font-semibold tabular-nums ${accent ? 'text-sky-900' : 'text-slate-900'}`}>
        {valeur}
      </p>
    </div>
  )
}
